import { type RefObject, useEffect } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Accessibility helper for the recipient flow's custom (non-MUI) modals — the
 * filter sheet, gift-message card, and video overlay — which manage their own
 * visibility via CSS classes rather than mounting/unmounting.
 *
 * While `active`, it: moves focus into the dialog on open (so screen readers
 * announce it), traps Tab/Shift+Tab within the dialog so focus can't slip behind
 * the scrim, and restores focus to the previously-focused element (the trigger)
 * on close. Pairs with the existing `aria-modal`/Escape handling. WCAG 2.4.3.
 *
 * The dialog container ref must be focusable (give it `tabIndex={-1}`).
 */
export function useFocusTrap<T extends HTMLElement>(active: boolean, ref: RefObject<T | null>) {
  useEffect(() => {
    if (!active) return;
    const node = ref.current;
    if (!node) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Visible, tabbable elements within the dialog (excludes the CSS-hidden ones).
    const getFocusable = () =>
      Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null,
      );

    // Move focus into the dialog so its name is announced.
    node.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const focusable = getFocusable();
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const current = document.activeElement;
      if (event.shiftKey && (current === first || current === node)) {
        event.preventDefault();
        last?.focus();
      } else if (!event.shiftKey && current === last) {
        event.preventDefault();
        first?.focus();
      }
    };

    node.addEventListener("keydown", onKeyDown);
    return () => {
      node.removeEventListener("keydown", onKeyDown);
      // Restore focus to whatever opened the dialog.
      previouslyFocused?.focus?.();
    };
  }, [active, ref]);
}
